import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, Inject, Input, OnDestroy, Optional, Output, Self, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacyFormField as MatFormField, MatLegacyFormFieldControl as MatFormFieldControl, MAT_LEGACY_FORM_FIELD as MAT_FORM_FIELD } from '@angular/material/legacy-form-field';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatLegacyProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacySelect as MatSelect, MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { BehaviorSubject, Subject, Subscription, switchMap } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { SearchQueriesDebounceTime, SearchQueriesDebounceTimeBackend } from '../../../../helper/helper';
import { AppButtonModule } from '../../../app-button-module/app-button.module';
import { SelectSearchOption } from '../select-search/select-search.component';

@Component({
    selector: 'app-select-search-paged-options',
    standalone: true,
    imports: [
        CommonModule,
        FontAwesomeModule,
        MatButtonModule,
        MatOptionModule,
        MatSelectModule,
        NgxMatSelectSearchModule,
        ScrollingModule,
        ReactiveFormsModule,
        MatTooltipModule,
        AppButtonModule,
        MatPaginatorModule,
        MatLegacyProgressBarModule,
    ],
    templateUrl: './select-search-paged-options.component.html',
    styleUrls: ['./select-search-paged-options.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: MatFormFieldControl, useExisting: SelectSearchPagedOptionsComponent }],
    // eslint-disable-next-line @angular-eslint/no-host-metadata-property
    host: {
        '[id]': 'id',
    },
})
export class SelectSearchPagedOptionsComponent implements ControlValueAccessor, MatFormFieldControl<SelectSearchOption>, OnDestroy {
    static nextId = 0;
    @ViewChild('select') matSelect: MatSelect;
    @Input()
    public triggerTemplate: TemplateRef<any>;
    @Input()
    NoEntriesFoundLabel = 'keine Einträge gefunden';
    @Input()
    SearchPlaceholderLabel = 'Suchen ...';
    @Input()
    CompareOptions: (a: SelectSearchOption, b: SelectSearchOption) => boolean = (a, b) => a === b;
    @Input()
    OptionTemplate: TemplateRef<any>;
    @Input()
    set Options(options: SelectSearchOption[]) {
        this.filteredOptions$.next(options);
        this.stateChanges.next();
    }

    @Output() searchChange = new EventEmitter<string | null>();
    @Output() opened = new EventEmitter<void>();

    @Input()
    panelClass = '';
    @Input()
    isLoading = false;
    @Input()
    itemSize = 42;
    @Output()
    selectionChange = new EventEmitter<SelectSearchOption | null>();
    form = new FormControl<SelectSearchOption | null>(null);
    searchForm = new FormControl<string | null>(null);
    filteredOptions$: BehaviorSubject<SelectSearchOption[]> = new BehaviorSubject<SelectSearchOption[]>([]);
    selectedOption$ = new BehaviorSubject<SelectSearchOption | null>(null);
    shadowOption$ = this.filteredOptions$.pipe(
        switchMap((options) =>
            this.selectedOption$.pipe(
                map((value) => {
                    if (value) {
                        return options.find((o) => this.CompareOptions(o, value)) || value;
                    }
                    return null;
                }),
            ),
        ),
    );
    stateChanges = new Subject<void>();
    focused = false;
    touched = false;
    controlType = 'search-select-input';
    id = `search-select-input-${SelectSearchPagedOptionsComponent.nextId++}`;
    onTouched = () => {};
    subs: Subscription[] = [];

    get empty() {
        return !this.form.getRawValue();
    }

    @Input() userAriaDescribedBy: string;

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }
    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }
    private _placeholder: string;

    @Input()
    get required(): boolean {
        return this._required;
    }
    set required(value: BooleanInput) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    private _required = false;

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.form.disable() : this.form.enable();
        this.stateChanges.next();
    }
    private _disabled = false;

    @Input()
    get value(): SelectSearchOption | null {
        if (!this.form.invalid) {
            return this.form.value;
        }
        return null;
    }
    set value(tel: SelectSearchOption | null) {
        this.form.setValue(tel, { emitEvent: false });
        this.selectedOption$.next(this.value);
        this.stateChanges.next();
    }

    get errorState(): boolean {
        return (this.form.invalid || this.ngControl?.invalid) && this.touched;
    }
    private onChange: (value: SelectSearchOption | null) => void = () => {};

    constructor(private _focusMonitor: FocusMonitor, private _elementRef: ElementRef<HTMLElement>, @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField, @Optional() @Self() public ngControl: NgControl) {
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
        this.subs.push(
            this.searchForm.valueChanges
                .pipe(
                    debounceTime(SearchQueriesDebounceTimeBackend),
                    distinctUntilChanged((a, b) => (a || '') === (b || '')),
                )
                .subscribe(() => {
                    this.searchChange.emit(this.searchForm.value);
                }),
            this.form.valueChanges.subscribe((v) => this.selectedOption$.next(this.value)),
        );
    }

    @HostBinding('class.floating')
    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }
    autofilled?: boolean;

    ngOnDestroy() {
        this.stateChanges.complete();
        this._focusMonitor.stopMonitoring(this._elementRef);

        if (this.ngControl) {
            this.ngControl.valueAccessor = null;
        }


        for (let sub of this.subs) {
            sub.unsubscribe();
        }
    }

    onFocusIn(event: FocusEvent) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            this.touched = true;
            this.focused = false;
            this.onTouched();
            this.stateChanges.next();
        }
    }

    setDescribedByIds(ids: string[]) {
        this.matSelect?._elementRef.nativeElement.setAttribute('aria-describedby', ids.join(' '));
    }
    onSelectionChange(value: SelectSearchOption | null): void {

        this.selectionChange.emit(value);

        this.onChange(value);
    }
    onContainerClick() {
        this._focusMonitor.focusVia(this.matSelect._elementRef, 'program');
        if (!this.matSelect.panelOpen) {
            this.matSelect.open();
        }
    }
    writeValue(tel: SelectSearchOption | null): void {
        this.value = tel;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
}
